import React from 'react';

const ReceiptPrint = React.forwardRef(({ sale, shop }, ref) => {
  if (!sale) return null;

  const fi = sale.fiscal_invoice;
  const now = new Date(sale.created_at);
  const dateStr = now.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

  // Business info from fiscal invoice or shop
  const businessName = fi?.razon_social || shop?.business_name || shop?.name || 'VendFactura';
  const nif = fi?.nif_emisor || shop?.nif || '';
  const address = shop?.address || '';
  const city = shop?.city || '';
  const postalCode = shop?.postal_code || '';
  const addressLine = [address, postalCode, city].filter(Boolean).join(', ');

  // Fiscal invoice number
  const invoiceNum = fi ? `${fi.invoice_series}-${String(fi.invoice_number).padStart(5, '0')}` : null;
  const invoiceType = fi?.tipo_factura || '';

  const s = {
    receipt: { width: '280px', fontFamily: 'monospace', fontSize: '12px', color: '#000', padding: '8px' },
    center: { textAlign: 'center' },
    bold: { fontWeight: 'bold' },
    line: { borderTop: '1px dashed #000', margin: '6px 0' },
    doubleLine: { borderTop: '2px solid #000', margin: '6px 0' },
    row: { display: 'flex', justifyContent: 'space-between', margin: '1px 0' },
    small: { fontSize: '10px', color: '#444' },
    tiny: { fontSize: '9px', color: '#666' },
    qrBox: { textAlign: 'center', margin: '8px 0', padding: '6px', border: '1px solid #000' },
  };

  return (
    <div ref={ref} style={s.receipt}>
      {/* Header - Business Info */}
      <div style={{ ...s.center, marginBottom: '4px' }}>
        <div style={{ ...s.bold, fontSize: '14px' }}>{businessName}</div>
        {nif && <div style={s.small}>NIF: {nif}</div>}
        {addressLine && <div style={s.small}>{addressLine}</div>}
        {shop?.phone && <div style={s.small}>Tel: {shop.phone}</div>}
      </div>

      <div style={s.doubleLine} />

      {/* Invoice/Sale Number */}
      <div style={s.center}>
        {invoiceNum ? (
          <>
            <div style={s.bold}>FACTURA {invoiceType}</div>
            <div style={{ fontFamily: 'monospace', fontSize: '13px' }}>{invoiceNum}</div>
          </>
        ) : (
          <div style={s.bold}>TICKET #{sale.sale_number}</div>
        )}
        <div style={s.small}>{dateStr} {timeStr}</div>
      </div>

      <div style={s.line} />

      {/* Items */}
      {sale.items.map((item, i) => (
        <div key={i}>
          <div style={{ ...s.row, fontSize: '11px' }}>
            <span style={{ flex: 1 }}>{item.name || item.product_name}</span>
          </div>
          <div style={{ ...s.row, fontSize: '11px', paddingLeft: '8px' }}>
            <span>{item.quantity} x €{(item.price || item.unit_price || 0).toFixed(2)}</span>
            <span style={s.bold}>€{((item.quantity || 1) * (item.price || item.unit_price || 0)).toFixed(2)}</span>
          </div>
        </div>
      ))}

      <div style={s.line} />

      {/* IVA Breakdown (VeriFactu compliant) */}
      {fi?.iva_breakdown && fi.iva_breakdown.length > 0 ? (
        <>
          <div style={{ ...s.small, ...s.bold, marginBottom: '2px' }}>DESGLOSE IVA:</div>
          {fi.iva_breakdown.map((b, i) => (
            <div key={i} style={{ ...s.row, ...s.small }}>
              <span>Base {b.rate}%</span>
              <span>€{b.base.toFixed(2)} + €{b.cuota.toFixed(2)}</span>
            </div>
          ))}
          <div style={s.row}>
            <span>Base Imponible</span>
            <span>€{fi.base_imponible.toFixed(2)}</span>
          </div>
          <div style={s.row}>
            <span>IVA</span>
            <span>€{fi.cuota_iva.toFixed(2)}</span>
          </div>
        </>
      ) : (
        <>
          <div style={s.row}>
            <span>Subtotal</span>
            <span>€{sale.subtotal.toFixed(2)}</span>
          </div>
          <div style={s.row}>
            <span>IVA</span>
            <span>€{sale.tax_total.toFixed(2)}</span>
          </div>
        </>
      )}

      <div style={s.doubleLine} />

      {/* Total */}
      <div style={{ ...s.row, fontSize: '16px', ...s.bold }}>
        <span>TOTAL</span>
        <span>€{sale.total.toFixed(2)}</span>
      </div>

      <div style={s.line} />

      {/* Payment */}
      <div style={s.row}>
        <span>Pago: {sale.payment_method === 'cash' ? 'Efectivo' : sale.payment_method === 'card' ? 'Tarjeta' : sale.payment_method}</span>
      </div>
      {sale.payment_method === 'cash' && sale.cash_received > 0 && (
        <>
          <div style={s.row}>
            <span>Recibido</span>
            <span>€{sale.cash_received.toFixed(2)}</span>
          </div>
          {sale.change_given > 0 && (
            <div style={{ ...s.row, ...s.bold }}>
              <span>Cambio</span>
              <span>€{sale.change_given.toFixed(2)}</span>
            </div>
          )}
        </>
      )}

      {sale.customer_name && (
        <div style={{ ...s.small, marginTop: '4px' }}>Cliente: {sale.customer_name}</div>
      )}

      {/* VeriFactu Compliance Section */}
      {fi && (
        <>
          <div style={s.doubleLine} />

          {/* QR Verification URL */}
          {fi.qr_verification_url && (
            <div style={s.qrBox}>
              <div style={{ ...s.small, ...s.bold, marginBottom: '2px' }}>VERIFICACION AEAT</div>
              <div style={{ ...s.tiny, wordBreak: 'break-all' }}>{fi.qr_verification_url}</div>
            </div>
          )}

          {/* Hash */}
          <div style={{ ...s.center, ...s.tiny, marginTop: '4px' }}>
            <div>Hash: {fi.huella_hash?.substring(0, 32)}</div>
          </div>

          {/* VeriFactu Legend */}
          <div style={{ ...s.center, ...s.bold, margin: '6px 0 2px', fontSize: '11px' }}>
            VERI*FACTU
          </div>
          <div style={{ ...s.center, ...s.tiny }}>
            Factura verificable en
          </div>
          <div style={{ ...s.center, ...s.tiny }}>
            sede.agenciatributaria.gob.es
          </div>
        </>
      )}

      {/* Footer */}
      <div style={s.line} />
      <div style={{ ...s.center, ...s.tiny, marginTop: '4px' }}>
        Gracias por su compra
      </div>
      <div style={{ ...s.center, ...s.tiny }}>
        {businessName}
      </div>
    </div>
  );
});

ReceiptPrint.displayName = 'ReceiptPrint';
export default ReceiptPrint;
